import api from './api';
import { ApiResponse, unwrapApiResponse } from '@/utils/api-response.util';

const BASE_URL = '/health-index';

export type HealthIndexCalculatorName = 'daily-resource' | 'rolling-index' | 'baseline-index';

export type ResourceBlockWeights = { A: number; F: number; T: number; N: number; W: number };

export type SixDirectionsMatrix = {
  physical: ResourceBlockWeights;
  energy: ResourceBlockWeights;
  clarity: ResourceBlockWeights;
  productivity: ResourceBlockWeights;
  focus: ResourceBlockWeights;
  emotional: ResourceBlockWeights;
};

export type IndexLevelRow = { level: 'low' | 'unstable' | 'good' | 'strong'; label: string; min: number };
export type BaselineStatusRow = { status: 'start' | 'forming' | 'preliminary' | 'full'; label: string; maxDays: number | null };
export type RatingScale = { min: number; max: number; stepPercent: number };
// v1.3 — коридор для N (харчування) і S (сон): 100 балів у [fullRangeMin, fullRangeMax],
// лінійний спад до 0 на межах [zeroBelow, zeroAbove]. Продукт явно лишає межі відкритими
// (spec-condensed-v1.3.md розділ 16), тому конфігуровані.
export type CorridorThresholds = { fullRangeMin: number; fullRangeMax: number; zeroBelow: number; zeroAbove: number };

export type HealthIndexConfig = {
  sixDirectionsMatrix: SixDirectionsMatrix;
  indexLevels: IndexLevelRow[];
  rollingWindowDays: number;
  baselineWindowDays: number;
  baselineStatuses: BaselineStatusRow[];
  ratingScale: RatingScale;
  nutritionCorridor: CorridorThresholds;
  sleepCorridor: CorridorThresholds;
  // Мінімальна зміна відображуваного EHI (день до дня, ціле число), щоб надіслати пуш "EHI змінився"
  // (2026-09-24, за проханням користувача) - `EhiSummariesCollectorService.notifyIfChanged` на бекенді.
  ehiChangeNotificationThreshold: number;
};

export type CalculatorSummary = { name: HealthIndexCalculatorName; caption: string };

export type CalculatorInfo = {
  name: HealthIndexCalculatorName;
  caption: string;
  description: string;
  requiredParams: string[];
  exampleParams: any;
  exampleExplanation: string;
  defaultConfig: HealthIndexConfig;
};

export type CalculateResponse<TResult = any> = {
  calculator: HealthIndexCalculatorName;
  result: TResult;
  explanation: string;
  resultDetails?: Record<string, any>;
  calculatedAt: string;
};

export const getAvailableCalculators = async (): Promise<CalculatorSummary[]> => {
  const response = await api.get<ApiResponse<CalculatorSummary[]>>(BASE_URL);
  return unwrapApiResponse(response);
};

export const getCalculatorInfo = async (name: HealthIndexCalculatorName): Promise<CalculatorInfo> => {
  const response = await api.get<ApiResponse<CalculatorInfo>>(`${BASE_URL}/${name}/info`);
  return unwrapApiResponse(response);
};

export const calculateHealthIndex = async <TResult = any>(
  name: HealthIndexCalculatorName,
  params: any,
  config?: HealthIndexConfig | null,
): Promise<CalculateResponse<TResult>> => {
  const response = await api.post<ApiResponse<CalculateResponse<TResult>>>(`${BASE_URL}/${name}/calculate`, { params, config });
  return unwrapApiResponse(response);
};
